import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Fragment,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  type Ref,
  type ComponentPropsWithoutRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { GroupedTableVirtuoso } from 'react-virtuoso';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import classNamesBind from 'classnames/bind';
import cn from 'classnames';
import BaseTableHeadReport, { BaseTableHeadRowReport } from './base-table-report/BaseTableHeadReport';
import reportBaseTableStyles from './base-table-report/BaseTableReport.module.scss';
import reportPageStyles from '../Report.module.scss';
import type { HeadCelType } from '~/utils/types/common';
import type { SortDirection } from '~/utils/types/common';
import type { Bc01TableRow } from './constants';
import { ReportTableMultilineCell } from './ReportTableMultilineCell';

const btcx = classNamesBind.bind(reportBaseTableStyles);
const rcx = classNamesBind.bind(reportPageStyles);

dayjs.extend(customParseFormat);

/** When total data rows meet or exceed this, the body is virtualized (group headers stay grouped). */
const VIRTUALIZE_ROW_THRESHOLD = 80;

const DATA_CELL_CLASS =
  'text-base font-normal text-[#161A1F] text-left align-top whitespace-normal';

/** Same size as body cells; one step bolder than font-normal. */
const GROUP_DATE_CELL_CLASS =
  'border-0 py-2.5 px-4 text-left text-base font-medium text-[#161A1F] whitespace-nowrap';

export type ReportDateGroup = {
  dateGroup: string;
  items: Bc01TableRow[];
};

function formatGroupDateLabel(raw: string): string {
  if (!raw) return '—';
  if (dayjs(raw, 'DD-MM-YYYY', true).isValid()) {
    return dayjs(raw, 'DD-MM-YYYY').format('DD/MM/YYYY');
  }
  if (dayjs(raw).isValid()) {
    return dayjs(raw).format('DD/MM/YYYY');
  }
  return raw;
}

const ReportDataCell = memo(function ReportDataCell({ columnLabel, raw }: { columnLabel?: string; raw: string }) {
  const isNgay = columnLabel === 'Ngày';
  const isDiaDiem = columnLabel === 'Địa điểm bắt đầu' || columnLabel === 'Địa điểm kết thúc';
  let display = raw;
  if (isNgay) {
    if (dayjs(raw, 'DD-MM-YYYY', true).isValid()) {
      display = dayjs(raw, 'DD-MM-YYYY').format('DD/MM/YYYY');
    } else if (dayjs(raw).isValid()) {
      display = dayjs(raw).format('DD/MM/YYYY');
    }
  }
  return (
    <TableCell className={`${DATA_CELL_CLASS}${isDiaDiem ? ' max-w-[220px]' : ''}`}>
      <ReportTableMultilineCell text={display} />
    </TableCell>
  );
});

function useDateColumnId(headCells: HeadCelType[]): string {
  return useMemo(() => {
    const i = headCells.findIndex((h) => h.label === 'Ngày');
    return i >= 0 ? `col_${i}` : 'col_0';
  }, [headCells]);
}

export function useReportRowsGroupedByDate(rows: Bc01TableRow[], headCells: HeadCelType[]): ReportDateGroup[] {
  const dateColumnId = useDateColumnId(headCells);

  return useMemo(() => {
    if (rows.length === 0 || headCells.length === 0) return [];

    const map = new Map<string, Bc01TableRow[]>();
    for (const row of rows) {
      const raw = row[dateColumnId] ?? '';
      const label = formatGroupDateLabel(raw);
      const existing = map.get(label);
      if (existing) existing.push(row);
      else map.set(label, [row]);
    }

    // Keep group order aligned with incoming row order (which is already sorted by parent table state).
    const keys = Array.from(map.keys());

    return keys.map((dateGroup) => ({
      dateGroup,
      items: map.get(dateGroup) ?? [],
    }));
  }, [rows, headCells, dateColumnId]);
}

type AggregateReportGroupedTableProps = {
  headCells: HeadCelType[];
  rows: Bc01TableRow[];
  sortBy?: string;
  sortOrder: SortDirection;
  onRequestSort: (field: string, order: SortDirection) => void;
  /** Thay thế `noData` khi bảng trống (vd. timeout / payload lớn). */
  emptyMessage?: string;
  /** Khi true, không hiển thị dòng “không có dữ liệu” (đang loading). */
  suppressEmptyState?: boolean;
};

function renderDataRow(headCells: HeadCelType[], row: Bc01TableRow): ReactElement {
  return (
    <>
      {headCells.map((hc) => (
        <ReportDataCell key={hc.id} columnLabel={hc.label} raw={row[hc.id] ?? ''} />
      ))}
    </>
  );
}

const GROUP_ROW_BG = '#E8F1FB';

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === 'function') ref(value);
  else (ref as MutableRefObject<T | null>).current = value;
}

function shouldIgnoreDragStart(target: EventTarget | null) {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      'input, textarea, button, a, label, svg, [role="button"], [role="checkbox"], .MuiCheckbox-root, .MuiIconButton-root, .MuiInputBase-root, .MuiOutlinedInput-root'
    )
  );
}

function useDragScrollHandlers(scrollEl: HTMLDivElement | null) {
  const dragRef = useRef<{ startX: number; scrollLeft: number } | null>(null);

  useEffect(() => {
    if (!scrollEl) return;

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (shouldIgnoreDragStart(e.target)) return;
      dragRef.current = { startX: e.clientX, scrollLeft: scrollEl.scrollLeft };
      scrollEl.style.cursor = 'grabbing';
      scrollEl.style.userSelect = 'none';
    };

    const onMouseMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      e.preventDefault();
      const dx = e.clientX - d.startX;
      scrollEl.scrollLeft = d.scrollLeft - dx;
    };

    const endDrag = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      scrollEl.style.cursor = '';
      scrollEl.style.userSelect = '';
    };

    scrollEl.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', endDrag);
    return () => {
      scrollEl.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', endDrag);
    };
  }, [scrollEl]);
}

const DEFAULT_TABLE_SX = { minWidth: 'max-content' } as const;

const AggregateTableScroller = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function AggregateTableScroller(
  props,
  forwardedRef
) {
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      setScrollEl(node);
      assignRef(forwardedRef, node);
    },
    [forwardedRef]
  );

  useDragScrollHandlers(scrollEl);

  const { style, className, ...rest } = props;
  return (
    <TableContainer
      component='div'
      ref={setRefs}
      className={cn('rounded-t-xl border-b-0 bg-white', rcx('aggregate-table-scroll'), className)}
      style={style}
      {...rest}
    />
  );
});
AggregateTableScroller.displayName = 'AggregateTableScroller';

const AggregateVirtuosoTable = forwardRef<HTMLTableElement, ComponentPropsWithoutRef<typeof Table>>((props, ref) => {
  const { className, style, ...rest } = props;
  return (
    <Table
      ref={ref}
      stickyHeader
      className={`w-full border-separate border-spacing-0${className ? ` ${className}` : ''}`}
      sx={DEFAULT_TABLE_SX}
      style={style}
      {...rest}
    />
  );
});
AggregateVirtuosoTable.displayName = 'AggregateVirtuosoTable';

const AggregateVirtuosoBodyRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(function AggregateVirtuosoBodyRow(
  props,
  ref
) {
  return (
    <TableRow
      ref={ref}
      {...props}
      hover
      tabIndex={-1}
      sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
    />
  );
});

const ReportAggregateScrollContainer = memo(function ReportAggregateScrollContainer({ children }: { children: ReactNode }) {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  useDragScrollHandlers(el);
  return (
    <TableContainer component='div' ref={setEl} className={cn('rounded-t-xl border-b-0 bg-white', rcx('aggregate-table-scroll'))}>
      {children}
    </TableContainer>
  );
});

const AggregateReportGroupedTable = memo(function AggregateReportGroupedTable({
  headCells,
  rows,
  sortBy,
  sortOrder,
  onRequestSort,
  emptyMessage,
  suppressEmptyState,
}: AggregateReportGroupedTableProps) {
  const { t } = useTranslation('admin/common');
  const grouped = useReportRowsGroupedByDate(rows, headCells);
  const colCount = headCells.length;

  const shouldVirtualize = rows.length >= VIRTUALIZE_ROW_THRESHOLD;

  const virtualModel = useMemo(() => {
    if (!shouldVirtualize) return null;
    const groupCounts: number[] = [];
    const groupTitles: string[] = [];
    const flatRows: Bc01TableRow[] = [];
    for (const g of grouped) {
      groupTitles.push(g.dateGroup);
      groupCounts.push(g.items.length);
      flatRows.push(...g.items);
    }
    return { groupCounts, groupTitles, flatRows };
  }, [grouped, shouldVirtualize]);

  if (grouped.length === 0) {
    const hideEmpty = Boolean(suppressEmptyState);
    return (
      <Box className={btcx('table-container')} sx={{ position: 'relative' }}>
        <ReportAggregateScrollContainer>
          <Table stickyHeader sx={DEFAULT_TABLE_SX}>
            <BaseTableHeadReport
              headCells={headCells}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onRequestSort={onRequestSort}
            />
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={Math.max(colCount, 1)}
                  align='center'
                  sx={{
                    borderBottom: 'none',
                    py: 6,
                    verticalAlign: 'middle',
                    minHeight: 'min(50vh, 400px)',
                  }}
                  role={hideEmpty ? undefined : 'status'}
                  aria-hidden={hideEmpty ? true : undefined}
                >
                  {!hideEmpty && (
                    <Typography className='text-base text-[#404854] px-4 text-center'>
                      {emptyMessage ?? t('noData')}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </ReportAggregateScrollContainer>
      </Box>
    );
  }

  if (shouldVirtualize && virtualModel !== null) {
    const { groupCounts, groupTitles, flatRows } = virtualModel;

    return (
      <Box className={btcx('table-container')}>
        <GroupedTableVirtuoso<Bc01TableRow>
          style={{ height: '100%' }}
          data={flatRows}
          groupCounts={groupCounts}
          defaultItemHeight={72}
          overscan={12}
          computeItemKey={(_index, row) => row.id}
          components={{
            Scroller: AggregateTableScroller,
            Table: AggregateVirtuosoTable,
            TableRow: AggregateVirtuosoBodyRow,
          }}
          fixedHeaderContent={() => (
            <BaseTableHeadRowReport
              headCells={headCells}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onRequestSort={onRequestSort}
            />
          )}
          groupContent={(groupIndex) => (
            <TableCell
              colSpan={colCount}
              className={GROUP_DATE_CELL_CLASS}
              sx={{ bgcolor: GROUP_ROW_BG, borderBottom: '1px solid #EBF1F8' }}
            >
              {groupTitles[groupIndex] ?? ''}
            </TableCell>
          )}
          itemContent={(_index, _groupIndex, row) => renderDataRow(headCells, row)}
        />
      </Box>
    );
  }

  return (
    <Box className={btcx('table-container')}>
      <ReportAggregateScrollContainer>
        <Table stickyHeader sx={DEFAULT_TABLE_SX}>
          <BaseTableHeadReport
            headCells={headCells}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRequestSort={onRequestSort}
          />
          <TableBody>
            {grouped.map((group) => (
              <Fragment key={group.dateGroup}>
                <TableRow>
                  <TableCell
                    colSpan={colCount}
                    className={GROUP_DATE_CELL_CLASS}
                    sx={{ bgcolor: GROUP_ROW_BG, borderBottom: '1px solid #EBF1F8' }}
                  >
                    {group.dateGroup}
                  </TableCell>
                </TableRow>
                {group.items.map((row) => (
                  <TableRow
                    hover
                    key={row.id}
                    tabIndex={-1}
                    sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                  >
                    {renderDataRow(headCells, row)}
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </ReportAggregateScrollContainer>
    </Box>
  );
});

export default AggregateReportGroupedTable;
